<template>
	<BaseLayout :background-color-class="backgroundColorClass" :content-full-height="true" data-test="base-layout">
		<template #header>
			<HeaderUnified
				classes="bg-color-white"
				cancel-click-text="Icon-close"
				:cancel-link="{ router: 'home' }"
				data-test="header-unified"
			>
				<template #back>
					<BaseLink
						v-if="isOrderReview"
						:link="{ router: 'checkout-acknowledgments' }"
						class="text-color-content-primary display-flex align-items-center header-back-link"
					>
						<BaseSvgIcon name="chevron" dir="left" width="24" height="24" />
						<span :class="appStore.isMobile ? 'body-sm' : 'body-md'">Back</span>
					</BaseLink>

					<BaseLink
						v-else-if="isEntityAccountDetailsStep"
						:link="{ router: 'checkout-account-type' }"
						class="text-color-content-primary display-flex align-items-center header-back-link"
					>
						<BaseSvgIcon name="chevron" dir="left" width="24" height="24" />
						<span :class="appStore.isMobile ? 'body-sm' : 'body-md'">Back</span>
					</BaseLink>

					<CheckoutBackButton
						v-else-if="!shouldHideBackButton"
						:disabled="isBackButtonDisabled"
						:display-icon="true"
						:mixpanel-custom-properties="signupStore.mixpanelCustomProperties"
						classes="text-color-content-primary display-flex align-items-center"
					/>
				</template>
			</HeaderUnified>
		</template>

		<section class="container">
			<div
				:class="!appStore.isMobile ? 'signup-module' : 'col-12-smo col-4-md col-offset-4-md'"
				data-test="signup-module"
			>
				<slot>
					<router-view v-slot="{ Component }">
						<TransitionSubpage>
							<component :is="Component" />
							<template #bottom-content> </template>
						</TransitionSubpage>
					</router-view>
				</slot>
			</div>
		</section>
	</BaseLayout>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import CheckoutBackButton from '@components/checkout/checkout-back-button.vue';
import HeaderUnified from '@components/header/header-unified.vue';
import TransitionSubpage from '@components/layout/transition-subpage.vue';
import { useAppStore } from '@stores/app';
import { useSignupStore } from '@stores/signup';

const route = useRoute();

const appStore = useAppStore();
const signupStore = useSignupStore();

const isBackButtonDisabled = computed(() => {
	return signupStore.isBackButtonDisabled;
});

const shouldHideBackButton = computed((): boolean => {
	const unsupportedBackRoutes = ['checkout-personal-info', 'checkout-password'];

	const name = route.name;

	if (typeof name !== 'string') {
		return true;
	}

	return unsupportedBackRoutes.includes(name);
});

const backgroundColorClass = computed((): string => {
	return !appStore.isMobile && !route.path.includes('ira') ? 'bg-color-bg-secondary' : 'bg-color-white';
});

const isOrderReview = computed((): boolean => route.name === 'checkout-review-order');

const isEntityAccountDetailsStep = computed((): boolean => {
	const name = route.name;

	if (typeof name !== 'string') {
		return false;
	}

	return (
		name.includes('checkout-entity-account-details') ||
		name.includes('checkout-joint-account-details') ||
		name.includes('checkout-trust-account-details')
	);
});

useHead({
	title: 'Open Your Account',
	meta: [{ property: 'og:title', content: 'Open Your Account' }]
});
</script>

<style scoped>
:deep(a.header-back-link:hover),
:deep(a.header-back-link:active),
:deep(a.header-back-link:focus) {
	color: inherit;
}
</style>
